import "./Header.scss";
import {
  GridPresetWideContent,
  GridRow,
  Logo,
  HeaderItem,
  HeaderItemMobile,
  HideFor,
  SearchField,
  SearchButton,
  LanguageSwitcher,
  XCloseIcon,
  SkipLink,
} from "/App.Components";
import { useState, useRef, useEffect } from "react";
import { HeaderProps } from "/Models/Generated/HeaderProps";
import cn from "classnames";
import { Collapse } from "react-collapse";
import useClickOutsideWithExcludedClass from "/Helpers/Hooks/useClickOutsideWithExcludedClass.hook";
import useAutocompleteSuggestions from "/Helpers/Hooks/useAutocompleteSuggestions";
import { SearchFieldProps } from "/Models/Generated/SearchFieldProps";

interface Props extends HeaderProps {
  showOverlayDesktop: (show: boolean) => void;
  showOverlayMobile: (show: boolean) => void;
}

const doSearch = (
  searchTerm: string,
  searchField: SearchFieldProps | undefined,
) => {
  if (typeof searchField?.searchPageUrl === "undefined") return;
  window.location.href = `${searchField.searchPageUrl}?language=${searchField.language}&term=${encodeURIComponent(searchTerm)}`;
};

const HeaderDesktop = ({
  navigationLinks,
  languageSwitcher,
  searchField,
  mainNavigationAriaLabel = "Hovedmeny",
  showOverlayDesktop,
}: Props) => {
  const [searchFieldOpen, setSearchFieldOpen] = useState<boolean>(false);
  const [isItemOpen, setIsItemOpen] = useState<boolean[]>(
    Array(navigationLinks?.length ?? 0).fill(false),
  );
  const searchContainerRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const autocomplete = useAutocompleteSuggestions(
    searchField?.endpointAutocomplete,
    searchField?.language,
  );

  const handleClickOutside = () => setSearchFieldOpen(false);

  useClickOutsideWithExcludedClass(
    searchContainerRef,
    handleClickOutside,
    "click",
    "search-button-norad",
  );

  useClickOutsideWithExcludedClass(
    searchContainerRef,
    handleClickOutside,
    "focusout",
  );

  useEffect(() => {
    function handleEscapeKey(event: KeyboardEvent) {
      if (event.code === "Escape") {
        searchFieldOpen && setSearchFieldOpen(false);
      }
    }

    document.addEventListener("keydown", handleEscapeKey);
    return () => document.removeEventListener("keydown", handleEscapeKey);
  }, [searchFieldOpen]);

  useEffect(() => {
    showOverlayDesktop(
      searchFieldOpen || isItemOpen.filter((isOpen) => isOpen).length > 0,
    );
  }, [searchFieldOpen, isItemOpen]);

  return (
    <>
      <div className="header-norad__top-navigation">
        {languageSwitcher && <LanguageSwitcher languages={languageSwitcher} />}
        <div ref={searchContainerRef} className="header-norad__search">
          <SearchButton
            style={"primary"}
            openLabel={searchField?.openSearchAriaLabel ?? ""}
            closeLabel={searchField?.closeSearchAriaLabel}
            isOpen={searchFieldOpen}
            onClick={() => setSearchFieldOpen(!searchFieldOpen)}
          />
          <Collapse
            isOpened={searchFieldOpen}
            theme={{ collapse: "header-norad__search-expanded" }}
          >
            <SearchField
              tabIndex={searchFieldOpen ? 0 : -1}
              id="searchfield-desktop"
              searchButtonAriaLabel={searchField?.searchButtonAriaLabel ?? ""}
              searchPlaceholder={searchField?.searchPlaceholder ?? ""}
              setSearchTerm={setSearchTerm}
              searchTerm={searchTerm}
              autocompleteItems={autocomplete.items}
              setAutocompleteItems={autocomplete.setItems}
              doAutocomplete={autocomplete.fetchSuggestions}
              doSearch={(term) => doSearch(term, searchField)}
            />
          </Collapse>
        </div>
      </div>
      {navigationLinks && (
        <nav
          className="header-norad__navigation"
          aria-label={mainNavigationAriaLabel}
        >
          <ul className="header-norad__navigation-buttons">
            {navigationLinks.map((item, idx) => (
              <HeaderItem
                key={item.menuName}
                {...item}
                isOpen={isItemOpen[idx]}
                setIsOpen={(isOpen) => {
                  setIsItemOpen((prevState) => {
                    const copy = [...prevState];
                    copy[idx] = isOpen;
                    return copy;
                  });
                }}
              />
            ))}
          </ul>
        </nav>
      )}
    </>
  );
};

const HeaderMobile = ({
  navigationLinks,
  languageSwitcher,
  searchField,
  closeMenuAriaLabel,
  openMenuAriaLabel,
  menuButtonText,
  mainNavigationAriaLabel = "Hovedmeny",
  showOverlayMobile,
}: Props) => {
  const [searchFieldOpen, setSearchFieldOpen] = useState<boolean>(false);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const menuContainerRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const autocomplete = useAutocompleteSuggestions(
    searchField?.endpointAutocomplete,
    searchField?.language,
  );

  const onOpenMenuButtonClick = () => {
    setMenuIsOpen(!menuIsOpen);
    setSearchFieldOpen(false);
  };

  const handleClickOutsideSearch = () => {
    setSearchFieldOpen(false);
  };

  const handleClickOutsideMenu = () => {
    setMenuIsOpen(false);
  };

  useEffect(
    () => showOverlayMobile(menuIsOpen || searchFieldOpen),
    [menuIsOpen, searchFieldOpen],
  );

  useClickOutsideWithExcludedClass(
    searchContainerRef,
    handleClickOutsideSearch,
    "click",
    "search-button-norad",
  );

  useClickOutsideWithExcludedClass(
    searchContainerRef,
    handleClickOutsideSearch,
    "focusout",
  );

  useClickOutsideWithExcludedClass(
    menuContainerRef,
    handleClickOutsideMenu,
    "click",
    "header-norad__mobile-menu-button",
  );

  useClickOutsideWithExcludedClass(
    menuContainerRef,
    handleClickOutsideMenu,
    "focusout",
  );

  useEffect(() => {
    function handleEscapeKey(event: KeyboardEvent) {
      if (event.code === "Escape") {
        searchFieldOpen && setSearchFieldOpen(false);
        if (menuIsOpen) {
          setMenuIsOpen(false);
          // Focus would otherwise be left on an element that is now hidden.
          menuButtonRef.current?.focus();
        }
      }
    }

    document.addEventListener("keydown", handleEscapeKey);
    return () => document.removeEventListener("keydown", handleEscapeKey);
  }, [searchFieldOpen, menuIsOpen]);

  useEffect(() => {
    const menuContainer = menuContainerRef.current;
    if (!menuIsOpen || !menuContainer) return;

    const focusableSelector =
      'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])';

    const getFocusableElements = () =>
      Array.from(menuContainer.querySelectorAll<HTMLElement>(focusableSelector))
        .filter((element) => !element.hasAttribute("disabled"))
        .filter((element) => element.offsetParent !== null);

    const trapMenuFocus = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const activeElement = document.activeElement as HTMLElement | null;
      const currentIndex = activeElement
        ? focusableElements.indexOf(activeElement)
        : -1;

      // Let the browser handle every Tab that stays inside the menu, and only
      // step in where it would move focus out of it.
      const leavesMenu =
        currentIndex === -1 ||
        (event.shiftKey && currentIndex === 0) ||
        (!event.shiftKey && currentIndex === focusableElements.length - 1);

      if (!leavesMenu) return;

      const step = event.shiftKey ? -1 : 1;
      const nextIndex =
        currentIndex === -1
          ? 0
          : (currentIndex + step + focusableElements.length) %
            focusableElements.length;

      event.preventDefault();
      focusableElements[nextIndex].focus();
    };

    menuContainer.addEventListener("keydown", trapMenuFocus);
    return () => menuContainer.removeEventListener("keydown", trapMenuFocus);
  }, [menuIsOpen]);

  return (
    <div className="header-norad__buttons">
      <div ref={searchContainerRef}>
        <SearchButton
          style={"primary"}
          openLabel={searchField?.searchButtonAriaLabel ?? ""}
          closeLabel={searchField?.closeSearchAriaLabel}
          isOpen={searchFieldOpen}
          onClick={() => {
            setSearchFieldOpen((prev) => !prev);
            setMenuIsOpen(false);
          }}
        />
        <Collapse
          isOpened={searchFieldOpen}
          theme={{ collapse: "header-norad__search-expanded" }}
        >
          <SearchField
            tabIndex={searchFieldOpen ? 0 : -1}
            id="searchfield-mobile"
            searchButtonAriaLabel={searchField?.searchButtonAriaLabel ?? ""}
            searchPlaceholder={searchField?.searchPlaceholder ?? ""}
            setSearchTerm={setSearchTerm}
            searchTerm={searchTerm}
            autocompleteItems={autocomplete.items}
            setAutocompleteItems={autocomplete.setItems}
            doAutocomplete={autocomplete.fetchSuggestions}
            doSearch={(term) => doSearch(term, searchField)}
          />
        </Collapse>
      </div>
      <div ref={menuContainerRef}>
        <button
          ref={menuButtonRef}
          type="button"
          aria-label={menuIsOpen ? closeMenuAriaLabel : openMenuAriaLabel}
          aria-expanded={menuIsOpen}
          aria-haspopup={true}
          onClick={onOpenMenuButtonClick}
          className={cn("header-norad__mobile-menu-button", {
            "header-norad__mobile-menu-button--open": menuIsOpen,
          })}
        >
          {!menuIsOpen && <span>{menuButtonText}</span>}
          {menuIsOpen && <XCloseIcon size="24" thickness="Reg" />}
        </button>

        <Collapse
          isOpened={menuIsOpen}
          theme={{ collapse: "header-norad__navigation-content" }}
        >
          {navigationLinks && (
            <nav
              className="header-norad__navigation"
              aria-label={mainNavigationAriaLabel}
            >
              {languageSwitcher && (
                <LanguageSwitcher
                  tabIndex={menuIsOpen ? 0 : -1}
                  languages={languageSwitcher}
                />
              )}
              <ul className="header-norad__navigation-buttons">
                {navigationLinks.map((item) => (
                  <HeaderItemMobile
                    mobileMenuIsOpen={menuIsOpen}
                    key={item.menuName}
                    {...item}
                  />
                ))}
              </ul>
            </nav>
          )}
        </Collapse>
      </div>
    </div>
  );
};

const Header = ({
  startPageUrl,
  navigationLinks,
  logoAriaLabel,
  skipToMainContentText,
  languageSwitcher,
  searchField,
  closeMenuAriaLabel,
  openMenuAriaLabel,
  menuButtonText,
  mainNavigationAriaLabel,
  showOverlayDesktop,
  showOverlayMobile,
}: Props) => {
  const headerProps = {
    navigationLinks,
    languageSwitcher,
    searchField,
    closeMenuAriaLabel,
    openMenuAriaLabel,
    menuButtonText,
    mainNavigationAriaLabel,
    showOverlayDesktop,
    showOverlayMobile,
  };
  return (
    <header className="header-norad">
      <SkipLink text={skipToMainContentText ?? ""} />
      <GridRow>
        <GridPresetWideContent>
          <div className="header-norad__content">
            <div className="header-norad__logo">
              <Logo
                url={startPageUrl}
                ariaLabel={logoAriaLabel}
                theme="header"
              />
            </div>
            <HideFor desktop desktopLargeUp desktopMedium>
              <HeaderMobile {...headerProps} />
            </HideFor>

            <HideFor mobile tablet>
              <HeaderDesktop {...headerProps} />
            </HideFor>
          </div>
        </GridPresetWideContent>
      </GridRow>
    </header>
  );
};

export default Header;
